import React, { useEffect, useRef, useCallback } from 'react';
import { formatDuration } from '../../utils/video-utils';

/**
 * VideoPlayerModal Component
 * Full-screen modal that plays video with sound
 * 
 * Props:
 * - video: Video object { url, thumbnailUrl, duration }
 * - isOpen: Whether modal is open
 * - onClose: Close handler
 */
const VideoPlayerModal = ({ video, isOpen, onClose }) => {
  const videoRef = useRef(null);
  const modalRef = useRef(null);

  // Handle escape key to close
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  // Add/remove event listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      
      // Auto-play video when modal opens
      if (videoRef.current) {
        videoRef.current.play().catch((error) => {
          console.log('Auto-play was prevented:', error);
        });
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, handleKeyDown]);

  // Handle click outside video to close
  const handleBackdropClick = (e) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  if (!isOpen || !video) return null;

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Video player"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full text-white transition-colors"
        aria-label="Close video"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Video container */}
      <div className="relative max-w-4xl w-full max-h-[80vh]">
        <video
          ref={videoRef}
          src={video.url}
          poster={video.thumbnailUrl}
          controls
          autoPlay
          playsInline
          className="w-full h-full max-h-[80vh] rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          Your browser does not support the video tag.
        </video>

        {/* Video info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent pointer-events-none">
          <div className="flex items-center justify-between text-white text-sm">
            <span className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Work Order Video
            </span>
            <span>{formatDuration(video.duration)}</span>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="absolute bottom-4 left-0 right-0 text-center">
        <p className="text-white text-sm opacity-60">
          Press ESC or click outside to close
        </p>
      </div>
    </div>
  );
};

export default VideoPlayerModal;

